import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { generateClient } from "aws-amplify/api";
import { fetchAuthSession, getCurrentUser } from "aws-amplify/auth";

import {
  listCommunityActivities,
  listUpliftEvents,
  listCommunityMemberships,
  listCommunities,
} from "../graphql/queries.js";

import {
  createCommunityActivity,
  createUpliftEvent,
  updateCommunityActivity,
  deleteCommunityActivity,
} from "../graphql/mutations.js";
import { uploadData, getUrl } from "@aws-amplify/storage";

const client = generateClient();
const ProgressContext = createContext();

const STORAGE_ACTIVE_SEASON = "personal_progress_active_season";
const STORAGE_ACTIVITY_SEASON_MAP = "personal_progress_activity_season_map";
const VALID_SEASONS = new Set(["Spring", "Summer", "Autumn", "Winter"]);
const DEFAULT_SEASON = "Spring";

const listPersonalProgressForSeason = /* GraphQL */ `
  query ListPersonalProgressForSeason(
    $filter: ModelPersonalProgressFilterInput
  ) {
    listPersonalProgresses(filter: $filter, limit: 100) {
      items {
        id
        activeSeason
        updatedAt
      }
    }
  }
`;

const createPersonalProgressForSeason = /* GraphQL */ `
  mutation CreatePersonalProgressForSeason(
    $input: CreatePersonalProgressInput!
  ) {
    createPersonalProgress(input: $input) {
      id
      activeSeason
    }
  }
`;

function normalizeId(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim().toLowerCase();
}

function uniqueIds(values) {
  const seen = new Set();
  const out = [];
  (values || []).forEach((v) => {
    const n = normalizeId(v);
    if (!n || seen.has(n)) return;
    seen.add(n);
    out.push(String(v));
  });
  return out;
}

function readObjectFromStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function seasonStorageKey(baseKey, userId) {
  const normalizedUserId = normalizeId(userId);
  return normalizedUserId ? `${baseKey}:${normalizedUserId}` : null;
}

function assignSeasonToNewActivity(activityId, userId) {
  const activeSeasonKey = seasonStorageKey(STORAGE_ACTIVE_SEASON, userId);
  const activityMapKey = seasonStorageKey(STORAGE_ACTIVITY_SEASON_MAP, userId);
  if (!activityId || !activeSeasonKey || !activityMapKey) return;
  try {
    const activeSeason = localStorage.getItem(activeSeasonKey);
    if (!VALID_SEASONS.has(activeSeason)) return;
    const activityMap = readObjectFromStorage(activityMapKey);
    if (activityMap[activityId] === activeSeason) return;
    activityMap[activityId] = activeSeason;
    localStorage.setItem(activityMapKey, JSON.stringify(activityMap));
  } catch {
    // Ignore localStorage write failures.
  }
}

function getUserMonthIndex(userCreatedAtISO) {
  if (!userCreatedAtISO) return 1;
  const joined = new Date(userCreatedAtISO);
  const now = new Date();
  const months =
    (now.getFullYear() - joined.getFullYear()) * 12 +
    (now.getMonth() - joined.getMonth());
  return Math.min(Math.max(months + 1, 1), 12);
}

async function resolveCurrentUserWithRetry() {
  const maxAttempts = 28;
  const waitMs = 200;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    try {
      // Wait for tokens first; mobile startup can delay auth hydration.
      const session = await fetchAuthSession({ forceRefresh: attempt > 1 });
      if (!session?.tokens?.accessToken && attempt < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, waitMs));
        continue;
      }

      const user = await getCurrentUser();
      if (user) return user;
    } catch (e) {
      if (attempt === maxAttempts) return null;
    }

    await new Promise((resolve) => setTimeout(resolve, waitMs));
  }

  return null;
}

async function getActiveSeasonForUser(userID) {
  if (!userID) return DEFAULT_SEASON;

  try {
    const response = await client.graphql({
      query: listPersonalProgressForSeason,
      variables: { filter: { userID: { eq: userID } } },
    });
    const records = [
      ...(response.data?.listPersonalProgresses?.items || []),
    ].sort(
      (left, right) =>
        new Date(right.updatedAt || 0).getTime() -
        new Date(left.updatedAt || 0).getTime(),
    );
    const activeSeason = records.find((record) =>
      VALID_SEASONS.has(record?.activeSeason),
    )?.activeSeason;
    if (activeSeason) return activeSeason;

    if (records.length === 0) {
      await client.graphql({
        query: createPersonalProgressForSeason,
        variables: {
          input: {
            userID,
            year: new Date().getFullYear(),
            journeyYearStartMonth: 3,
            activeSeason: DEFAULT_SEASON,
            activeSeasonChangedAt: new Date().toISOString(),
            activeSeasonLastEngagementAt: new Date().toISOString(),
          },
        },
      });
    }
  } catch (error) {
    console.error("Failed to resolve active progress season:", error);
  }

  return DEFAULT_SEASON;
}

export function ProgressProvider({ children }) {
  const [activities, setActivities] = useState([]);
  const [uplifts, setUplifts] = useState([]);
  const [joinedCommunities, setJoinedCommunities] = useState([]);
  const [communityData, setCommunityData] = useState({
    communities: [],
    joinedIDs: [],
  });
  const [loading, setLoading] = useState(true);
  const [currentUserID, setCurrentUserID] = useState(null);
  const authHydrationRetryCount = useRef(0);

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    // If initial load completed before auth hydration, retry a few times.
    if (loading || currentUserID || authHydrationRetryCount.current >= 5)
      return;

    authHydrationRetryCount.current += 1;
    const retryTimer = setTimeout(() => {
      loadAll();
    }, 1200 * authHydrationRetryCount.current);
    return () => clearTimeout(retryTimer);
  }, [loading, currentUserID]);

  async function loadAll() {
    setLoading(true);
    try {
      const user = await resolveCurrentUserWithRetry();
      if (!user) {
        console.debug &&
          console.debug("ProgressContext.loadAll: unauthenticated");
      }
      const userID =
        user?.userId || user?.username || user?.attributes?.sub || null;
      setCurrentUserID(userID);
      const userAliases = uniqueIds([
        userID,
        user?.userId,
        user?.username,
        user?.attributes?.sub,
        user?.signInDetails?.loginId,
      ]);

      let persistedSelectedIDs = [];
      let persistedMap = {};
      try {
        const raw = localStorage.getItem("selectedCommunityIDs");
        if (raw) persistedSelectedIDs = JSON.parse(raw) || [];
        const mapRaw = localStorage.getItem("selectedCommunityMap");
        if (mapRaw) persistedMap = JSON.parse(mapRaw) || {};
      } catch (e) {
        persistedSelectedIDs = [];
        persistedMap = {};
      }

      if (!user) {
        const faux = (persistedSelectedIDs || []).map((id) => ({
          id,
          name: persistedMap[id] || "Community",
        }));
        setJoinedCommunities(faux);
        setCommunityData({
          communities: faux,
          joinedIDs: persistedSelectedIDs,
        });
        setActivities([]);
        setUplifts([]);
        setLoading(false);
        return;
      }

      const membershipsRes = await client.graphql({
        query: listCommunityMemberships,
        variables: {
          filter: {
            and: [
              { leftAt: { attributeExists: false } },
              {
                or: (userAliases.length > 0 ? userAliases : [userID]).flatMap(
                  (alias) => [
                    { owner: { eq: alias } },
                    { userID: { eq: alias } },
                  ],
                ),
              },
            ],
          },
        },
      });
      const memberships =
        membershipsRes.data?.listCommunityMemberships?.items ?? [];
      const joinedIDs = memberships.map((m) => m.communityID);

      const communitiesRes = await client.graphql({ query: listCommunities });
      const allCommunities = communitiesRes.data?.listCommunities?.items ?? [];

      // For authenticated users, trust live memberships from backend.
      // Persisted selections can become stale and should not expand visibility.
      const visibleIDs = Array.from(new Set([...(joinedIDs || [])]));
      const visibleCommunities = allCommunities.filter((c) =>
        visibleIDs.includes(c.id),
      );

      setJoinedCommunities(visibleCommunities);
      setCommunityData({
        communities: visibleCommunities,
        joinedIDs: visibleIDs,
      });

      const communityConditions = (visibleIDs || []).map((cid) => ({
        communities: { contains: cid },
      }));
      const ownerConditions = (
        userAliases.length > 0 ? userAliases : [userID]
      ).flatMap((alias) => [
        { userID: { eq: alias } },
        { owner: { eq: alias } },
      ]);
      const activityFilter = {
        or: [...ownerConditions, ...communityConditions],
      };

      const activityRes = await client.graphql({
        query: listCommunityActivities,
        variables: { filter: activityFilter },
      });
      const fetchedActivities =
        activityRes.data?.listCommunityActivities?.items ?? [];
      setActivities(fetchedActivities);

      const upliftRes = await client.graphql({ query: listUpliftEvents });
      setUplifts(upliftRes.data?.listUpliftEvents?.items ?? []);
    } catch (err) {
      console.error("ProgressContext.loadAll error", err);
    } finally {
      setLoading(false);
    }
  }

  function getMonthlyGrowthForUser(userID) {
    const map = Array.from({ length: 12 }).reduce((acc, _, i) => {
      acc[i] = { leaves: 0, flowers: 0, fruit: 0 };
      return acc;
    }, {});
    activities.forEach((a) => {
      if (a.userID !== userID) return;
      const idx = a.createdAt
        ? new Date(a.createdAt).getMonth()
        : Math.max(0, Math.min(11, (a.monthIndex || 1) - 1));
      map[idx].leaves += 1;
    });
    uplifts.forEach((u) => {
      if (!u.createdAt) return;
      const d = new Date(u.createdAt);
      const idx = d.getMonth();
      const giver = u.giverUserID || u.userID || u.giver || null;
      const receiver =
        u.receiverUserID ||
        u.receiver ||
        (u.postID && activities.find((a) => a.id === u.postID)?.userID) ||
        null;
      if (giver === userID && (u.emoji === "👏" || u.emoji === "🤍"))
        map[idx].flowers += 1;
      if (receiver === userID && u.emoji === "💡") map[idx].fruit += 1;
    });
    return map;
  }

  async function createActivity({
    text,
    mediaFiles = [],
    communities,
    visibility,
    actionName,
    threadID,
    season: seasonOverride,
    seasonSnapshot,
  }) {
    try {
      const user = await getCurrentUser();
      const userID = user?.userId || user?.username || user?.attributes?.sub;
      const userCreatedAt = user?.createdAt || user?.attributes?.createdAt;
      const monthIndex = getUserMonthIndex(userCreatedAt);

      const imageURLs = [];
      const videoURLs = [];
      const documentURLs = [];

      for (const file of mediaFiles || []) {
        if (file && typeof file === "object" && file.name && file.type) {
          const key = `uploads/${Date.now()}_${Math.random().toString(36).slice(2, 7)}_${file.name.replace(/\s+/g, "_")}`;
          try {
            console.debug("uploading media", {
              key,
              name: file.name,
              type: file.type,
            });
            const res = await uploadData({
              key,
              body: file,
              contentType: file.type,
            });
            console.debug("uploadData response", res);
            const storedKey = (res && (res.key || res.Key)) || key;
            let url;
            try {
              url = await getUrl({ key: storedKey });
              console.debug("getUrl response", { storedKey, url });
            } catch (e) {
              console.warn(
                "getUrl failed, attempting deterministic S3 URL fallbacks",
                e,
              );
              try {
                const outputs = require("../amplify_outputs.json");
                const bucket =
                  outputs.storage?.bucket_name ||
                  outputs.aws_user_files_s3_bucket;
                const region =
                  outputs.aws_region || outputs.aws_user_files_s3_bucket_region;
                // Ensure storedKey is safely encoded for inclusion in a URL path
                const encodedKey = (storedKey || "")
                  .split("/")
                  .map(encodeURIComponent)
                  .join("/");
                const candidates = [];
                if (bucket && region) {
                  // regional virtual-hosted style
                  candidates.push(
                    `https://${bucket}.s3.${region}.amazonaws.com/${encodedKey}`,
                  );
                  // global virtual-hosted style (some SDKs/regions use this)
                  candidates.push(
                    `https://${bucket}.s3.amazonaws.com/${encodedKey}`,
                  );
                  // path-style endpoints
                  candidates.push(
                    `https://s3.${region}.amazonaws.com/${bucket}/${encodedKey}`,
                  );
                  candidates.push(
                    `https://s3.amazonaws.com/${bucket}/${encodedKey}`,
                  );
                }
                // last resort: use storedKey raw
                candidates.push(storedKey);
                console.debug("S3 URL fallback candidates", {
                  storedKey,
                  candidates,
                });
                // pick the first candidate that looks like an http(s) URL
                url = candidates.find(
                  (c) => typeof c === "string" && /^(https?:)\/\//i.test(c),
                );
                if (!url) url = storedKey;
              } catch (err) {
                console.warn(
                  "S3 fallback construction failed, using storedKey",
                  err,
                );
                url = storedKey;
              }
            }
            if (file.type.startsWith("image/")) imageURLs.push(url);
            else if (file.type.startsWith("video/")) videoURLs.push(url);
            else documentURLs.push(url);
          } catch (e) {
            console.error("uploadData threw", e && (e.message || e));
            console.error(e && e.stack ? e.stack : e);
            if (file && file.type) {
              if (file.type.startsWith("image/"))
                imageURLs.push(file.name || file);
              else if (file.type.startsWith("video/"))
                videoURLs.push(file.name || file);
              else documentURLs.push(file.name || file);
            } else {
              // unknown file shape; push its string representation
              imageURLs.push(String(file));
            }
          }
        } else if (typeof file === "string") {
          if (/(mp4|webm)$/i.test(file)) videoURLs.push(file);
          else imageURLs.push(file);
        }
      }

      // Ensure we only send valid URLs to AppSync. The schema requires
      // `imageURLs`, `videoURLs`, and `documentURLs` to be AWSURL values;
      // passing File objects, bare filenames, or other shapes causes
      // "Variable 'imageURLs' has an invalid value." errors. Filter and
      // log any removed entries for diagnostics.
      const urlLike = (v) => typeof v === "string" && /^https?:\/\//i.test(v);
      const cleanImageURLs = (imageURLs || []).filter(urlLike);
      const cleanVideoURLs = (videoURLs || []).filter(urlLike);
      const cleanDocumentURLs = (documentURLs || []).filter(urlLike);
      const removedCount =
        (imageURLs?.length || 0) -
        cleanImageURLs.length +
        (videoURLs?.length || 0) -
        cleanVideoURLs.length +
        (documentURLs?.length || 0) -
        cleanDocumentURLs.length;
      if (removedCount > 0) {
        console.warn(
          `ProgressContext.createActivity: removed ${removedCount} non-URL media entries before GraphQL call`,
          { imageURLs, videoURLs, documentURLs },
        );
      }

      const createRes = await client.graphql({
        query: createCommunityActivity,
        variables: {
          input: {
            userID,
            communities,
            visibility,
            monthIndex,
            season: seasonOverride || (await getActiveSeasonForUser(userID)),
            seasonSnapshot,
            text,
            actionName,
            threadID,
            imageURLs: cleanImageURLs,
            videoURLs: cleanVideoURLs,
            documentURLs: cleanDocumentURLs,
            engagementCount: 0,
          },
        },
      });

      // GraphQL client may return an errors array even when no exception thrown
      try {
        console.error(
          "createCommunityActivity response:",
          JSON.stringify(createRes, null, 2),
        );
      } catch (e) {
        console.error("createCommunityActivity response (raw):", createRes);
      }

      if (createRes && createRes.errors && createRes.errors.length) {
        try {
          console.error(
            "createCommunityActivity GraphQL errors:",
            JSON.stringify(createRes.errors, null, 2),
          );
        } catch (e) {
          console.error(
            "createCommunityActivity GraphQL errors (raw):",
            createRes.errors,
          );
        }
        const first = createRes.errors[0];
        const msg =
          (first && (first.message || first.error || first.extensions)) ||
          JSON.stringify(first);
        throw new Error(`createCommunityActivity failed: ${msg}`);
      }

      const createdActivityId = createRes?.data?.createCommunityActivity?.id;
      assignSeasonToNewActivity(createdActivityId, currentUserID);
      await loadAll();
    } catch (err) {
      try {
        console.error("createActivity error", err && (err.message || err));
        console.error(err && err.stack ? err.stack : err);
      } catch (e) {
        console.error("createActivity logging failed", e);
      }
      throw err;
    }
  }

  async function giveUplift({ postID, receiverUserID, emoji }) {
    try {
      const user = await getCurrentUser();
      const giverUserID =
        user?.userId || user?.username || user?.attributes?.sub;
      if (!giverUserID) return;
      if (giverUserID === receiverUserID) return;
      const alreadyGiven = uplifts.find((u) => {
        const g = u.giverUserID || u.userID || u.giver;
        return u.postID === postID && g === giverUserID;
      });
      if (alreadyGiven) return;
      const tmpId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const season = await getActiveSeasonForUser(giverUserID);
      const optimistic = {
        id: tmpId,
        userID: giverUserID,
        giverUserID: giverUserID,
        postID,
        receiverUserID,
        emoji,
        season,
        createdAt: new Date().toISOString(),
        _optimistic: true,
      };
      setUplifts((prev) => [optimistic, ...(prev || [])]);
      setActivities((prev) =>
        (prev || []).map((a) =>
          a.id === postID
            ? { ...a, engagementCount: (a.engagementCount ?? 0) + 1 }
            : a,
        ),
      );
      await client.graphql({
        query: createUpliftEvent,
        variables: {
          input: { userID: giverUserID, postID, receiverUserID, emoji, season },
        },
      });
      await loadAll();
    } catch (err) {
      console.error("giveUplift error", err);
      try {
        setUplifts((prev) => (prev || []).filter((u) => !u._optimistic));
        setActivities((prev) =>
          (prev || []).map((a) =>
            a.id === postID
              ? {
                  ...a,
                  engagementCount: Math.max((a.engagementCount ?? 1) - 1, 0),
                }
              : a,
          ),
        );
      } catch (e) {
        /* ignore */
      }
    }
  }

  function getTreeStatsForUser(userID) {
    const flowers = uplifts.filter((u) => {
      const giver = u.giverUserID || u.userID || u.giver;
      return giver === userID && (u.emoji === "👏" || u.emoji === "🤍");
    }).length;
    const fruit = uplifts.filter((u) => {
      const receiver =
        u.receiverUserID ||
        u.receiver ||
        (u.postID && activities.find((a) => a.id === u.postID)?.userID);
      return receiver === userID && u.emoji === "💡";
    }).length;
    const leaves = activities.filter((a) => a.userID === userID).length;
    return { leaves, flowers, fruit };
  }

  async function updateActivity(input) {
    try {
      const res = await client.graphql({
        query: updateCommunityActivity,
        variables: { input },
      });
      await loadAll();
      return res;
    } catch (err) {
      console.error("updateActivity error", err);
      throw err;
    }
  }

  async function deleteActivity({ id }) {
    try {
      await client.graphql({
        query: deleteCommunityActivity,
        variables: { input: { id } },
      });
      await loadAll();
    } catch (err) {
      console.error("deleteActivity error", err);
      throw err;
    }
  }

  return (
    <ProgressContext.Provider
      value={{
        loading,
        activities,
        uplifts,
        joinedCommunities,
        communityData,
        setCommunityData,
        createActivity,
        giveUplift,
        getTreeStatsForUser,
        getMonthlyGrowthForUser,
        refresh: loadAll,
        updateActivity,
        deleteActivity,
        currentUserID,
      }}
    >
      {children}
    </ProgressContext.Provider>
  );
}

export function useProgress() {
  return useContext(ProgressContext);
}
